import React, { useState } from "react";
import Loader from "../../../shared/Loader.js";
import { Toast } from "../../../shared/Toast.js";
import API from "../../../services/api.js";
import { alphaformatDateTime } from "../../../utils/dateFormat.js";
import { EmitEvent } from "../../../services/socket.js";
import { useNavigate } from "react-router-dom";

const ModeOfPayment = ({ mode }) => {
  if (!mode) return <>N/A</>;
  const data = (() => {
    try { return typeof mode === "string" ? JSON.parse(mode) : mode; }
    catch { return null; }
  })();
  if (!data) return <>Invalid Data</>;
  return (
    <div>
      <div>Channel: {data.channel || "N/A"}</div>
      <div>UPI ID: {data.upi_id || "N/A"}</div>
      <div>MODE: {data.upi_instrument || "N/A"}</div>
      {data.upi_payer_account_number && <div>Account: {data.upi_payer_account_number}</div>}
      {data.upi_payer_ifsc && <div>IFSC: {data.upi_payer_ifsc}</div>}
      {data.proof && <a href={data.proof} target="_blank" rel="noopener noreferrer">View Proof</a>}
    </div>
  );
};

const isVideoUrl = (url) => url && /\.(mp4|webm|ogg|mov)(\?.*)?$/i.test(url);

export default function GoLiveButton({ row, handleGoToLive, goLiveLoadingIdx, rowIdx, actionHandlers }) {
  const [modalOpen, setModalOpen] = useState(false);
  const [paymentDetails, setPaymentDetails] = useState({});
  const [creativeDetails, setCreativeDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);

  const isLive = row.status === 7 && !(!!row?.order_id);
  const statusClass = isLive ? "bg-go_to_green text-white" : "bg-go_to_gray text-white";
  const { dateStr, timeStr } = alphaformatDateTime(paymentDetails.statement_created_at || "");

  const fetchPaymentDetails = async () => {
    const res = await API({ url: "/alpha_cst_designer/payment_details", method: "POST", data: { creative_id: row.creative_id } });
    if (res.status === "SUCCESS") setPaymentDetails(res.data);
    else Toast(res.message);
  };

  const fetchCreativeDetails = async () => {
    const res = await API({ url: "/alpha_cst_designer/getCreativeDetailsDiscount", method: "POST", data: { creative_id: row.creative_id } });
    if (res.status === "SUCCESS") setCreativeDetails(res.data[0]);
  };

  const openModal = () => {
    if (isLive) {
      setLoading(true);
      setModalOpen(true);
      Promise.all([fetchPaymentDetails(), fetchCreativeDetails()]).finally(() => setLoading(false));
    } else {
      Toast("error", "Cannot Go Live, Client Not Accepted or Payment Details not Found");
    }
  };

  const navigate = useNavigate();

  const confirmGoLive = async () => {
    if (confirmLoading) return;
    setConfirmLoading(true);
    const res = await handleGoToLive(row, row.creative_id, rowIdx);
    setConfirmLoading(false);
    if (res.data?.log?.length) {
      EmitEvent(row?.creative_id, res?.data?.log[0]);
      navigate(`/home/lineitems`);
    }
  };

  const cancelGoLive = () => {
    setPaymentDetails({});
    setCreativeDetails(null);
    setModalOpen(false);
  };

  const handleViewAdDetails = () => {
    cancelGoLive();
    actionHandlers?.viewAdDetails?.(row);
  };

  const latestDesign = creativeDetails?.latest_design;
  const videoUrl = creativeDetails?.video_urls;
  const headLine = creativeDetails?.head_line;
  const description = creativeDetails?.description;

  return (
    <>
      <button
        className={`px-3 py-1.5 rounded font-medium ${statusClass} flex items-center justify-center whitespace-nowrap`}
        onClick={openModal}
        disabled={goLiveLoadingIdx === rowIdx}
      >
        {goLiveLoadingIdx === rowIdx ? <Loader /> : "Create Order and Lineitem"}
      </button>

      {modalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 p-4">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 space-y-4">
              <h2 className="text-xl font-semibold">Confirm Order and Line Item Creation</h2>

              {/* Creative Preview */}
              <div className="border rounded-lg overflow-hidden bg-gray-50">
                <div className="px-3 py-2 bg-gray-100 border-b flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">Creative Preview</span>
                  {actionHandlers?.viewAdDetails && (
                    <button
                      onClick={handleViewAdDetails}
                      className="text-xs text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1"
                    >
                      View Ad Details
                      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  )}
                </div>

                {loading ? (
                  <div className="flex items-center justify-center h-32 text-gray-400 text-sm">Loading preview…</div>
                ) : (latestDesign || videoUrl || headLine || description) ? (
                  <div className="p-3 space-y-3">

                    {/* Latest design image — click opens in new tab */}
                    {latestDesign && !isVideoUrl(latestDesign) && (
                      <a href={latestDesign} target="_blank" rel="noopener noreferrer" className="block group relative">
                        <img
                          src={latestDesign}
                          alt="Latest Design"
                          className="w-full max-h-48 object-contain rounded border bg-white"
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 flex items-center justify-center transition-all rounded">
                          <span className="opacity-0 group-hover:opacity-100 bg-black/60 text-white text-xs px-2 py-1 rounded flex items-center gap-1">
                            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                            </svg>
                            Open full size
                          </span>
                        </div>
                      </a>
                    )}

                    {/* Video — inline player + open in new tab link */}
                    {videoUrl && isVideoUrl(videoUrl) && (
                      <div className="space-y-1">
                        <video
                          src={videoUrl}
                          controls
                          className="w-full max-h-48 rounded border bg-black"
                        />
                        <a
                          href={videoUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-xs text-blue-600 hover:underline w-fit"
                        >
                          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                          Open video in new tab
                        </a>
                      </div>
                    )}

                    {/* Headline */}
                    {headLine && (
                      <div>
                        <p className="text-[10px] text-gray-500 uppercase font-medium mb-0.5">Headline</p>
                        <p className="text-sm font-semibold text-gray-800 break-words">{headLine}</p>
                      </div>
                    )}

                    {/* Description */}
                    {description && (
                      <div>
                        <p className="text-[10px] text-gray-500 uppercase font-medium mb-0.5">Description</p>
                        <p className="text-xs text-gray-600 break-words">{description}</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-20 text-gray-400 text-sm">No preview available</div>
                )}
              </div>

              {/* Payment Details */}
              <div className="border rounded-lg p-3 bg-gray-50 space-y-2 text-gray-700 text-sm">
                <h3 className="font-medium text-gray-800">Payment Details</h3>
                <p><strong>Reference ID:</strong> {paymentDetails.payment_ref_id || "—"}</p>
                <p><strong>Received Amount:</strong> {Math.round(paymentDetails.received_amount) || 0}</p>
                <p><strong>Mode of Payment:</strong> <ModeOfPayment mode={paymentDetails.mode_of_payment} /></p>
                <p><strong>Transaction Date &amp; Time:</strong> {dateStr + " " + timeStr}</p>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-1">
                <button
                  className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
                  onClick={cancelGoLive}
                  disabled={confirmLoading}
                >
                  Cancel
                </button>
                {!confirmLoading ? (
                  <button
                    className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                    onClick={confirmGoLive}
                    disabled={loading}
                  >
                    Confirm
                  </button>
                ) : (
                  <button className="px-4 py-2 bg-green-600 text-white rounded flex items-center justify-center" disabled>
                    <Loader />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
